import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dateAndTime from "date-and-time";
import { isArray } from "lodash";

import { usePymbleApi } from "./usePymbleApi";

// const API_END_POINT = `${api.dtAdmin}/teacher-lessons`;
const API_END_POINT = "/timetable/admin/lesson/";

const formatDate = (value: string | string[]) => {
  const datestr: string =
    isArray(value) && value.length > 0 ? value[0].toString() : value.toString();
  return dateAndTime.format(new Date(Date.parse(datestr)), "YYYY-MM-DD");
};

/**
 * Hook for separate loading of lesson data (for teachers)
 * for components
 */
export function useTeacherLessons(
  startDate: string | null = null,
  endDate: string | null = null,
  absentTeacherId: string | null = null,
  coverTeacherId: string | null = null,
) {
  const [teacherLessons, setTeacherLessons] = useState<any>([]);
  const [teacherLessonsLoaded, setTeacherLessonsLoaded] = useState(false);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the data from api
  // let queryKey = ["teacher-lessons", startDate, absentTeacherId, coverTeacherId];
  let queryKey = ["teacher-lessons", startDate];
  if (absentTeacherId) {
    queryKey.push(absentTeacherId);
  }
  if (coverTeacherId) {
    queryKey.push(coverTeacherId);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const url =
        API_END_POINT +
        "?" +
        (startDate ? "start_date=" + formatDate(startDate) + "&" : "") +
        (endDate ? "end_date=" + endDate + "&" : "") +
        (absentTeacherId ? "absent_teacher_id=" + absentTeacherId + "&" : "") +
        (coverTeacherId ? "cover_teacher_id=" + coverTeacherId + "&" : "");
      const res = await callApi("GET", url);
      return res;
    },
    enabled: isAuthenticated,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 1000 * 10, // 10 seconds
  });

  // When the data is loaded, set the state
  useEffect(() => {
    if (data) {
      let lessons = preProcessLessons(data);

      // Sort by date
      lessons = sortLessons(lessons);

      if (lessons) {
        setTeacherLessons(lessons);
      }
      setTeacherLessonsLoaded(true);
    }
  }, [data]);

  const refreshTeacherLessons = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  // Only sort by start time at the moment
  const sortLessons = (data: any[]) => {
    return data.sort(function (a: any, b: any) {
      try {
        if (a && a.LessonStartTime && b && b.LessonStartTime) {
          var startA = a.LessonStartTime;
          var startB = b.LessonStartTime;
          if (startA < startB) {
            return -1;
          }
          if (startA > startB) {
            return 1;
          }
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });
  };

  // Decorate as required
  const preProcessLessons = (lesData: any) => {
    return lesData.map((a: any) => {
      // Transform Title:
      let title: string = a?.Title ? a?.Title : a?.Class?.Title ? a?.Class?.Title : a?.Type;
      a.Title = title;
      // // If a.Absence._id exists but is null then add a.Absence.Teacher.Name to Title
      // if (a.Absence && a.Absence._id === null) {
      //   a.Title = a.Title + ' - ' + a.Absence.Teacher.Name;
      // }

      // Transform PeriodName - remove chars starting from first ( if present
      let periodNameShort: string = a?.Class?.PeriodName;
      if (periodNameShort) {
        if (periodNameShort && periodNameShort.indexOf("(") > 0) {
          periodNameShort = periodNameShort.substring(0, periodNameShort.indexOf("("));
        }
        a.Class.PeriodNameShort = periodNameShort;
      }

      return a;
    });
  };

  return {
    isError,
    error,
    teacherLessons,
    refreshTeacherLessons,
    teacherLessonsLoaded,
  };
}
